import React, { useState, useRef, useMemo, useEffect } from 'react';
import { baseUrlApi } from '../../config';
import {
    Box,
    Button,
    VStack,
    HStack,
    Text,
    Image,
    IconButton,
    Input,
    Textarea,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Flex,
    Progress,
    SimpleGrid,
    Badge,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
} from '@chakra-ui/react';
import { FaCamera, FaTrash, FaUpload, FaImages, FaFilePdf, FaMagic } from 'react-icons/fa';
import { anexarArquivosDevolucao, removerArquivoDevolucao, dispararAnaliseIADevolucao } from '../../services/api';
import { compressImages } from '../../utils/compressImage';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const buildFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const base = (baseUrlApi || 'http://localhost:8000').replace(/\/api\/?$/, '');
    return `${base}/${path}`;
};

const getFileName = (path) => (typeof path === 'string' ? path.split('/').pop() : (path?.name ?? ''));

const isImagePath = (path) => {
    const ext = (typeof path === 'string' ? path.split('.').pop() : (path?.name?.split('.').pop() ?? '')).toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
};

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const DevolucoesFileUpload = ({ devolucaoId, onUploadSuccess, existingFiles = [], preAnaliseExistente = '' }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [preAnalise, setPreAnalise] = useState(preAnaliseExistente);
    const [uploadStage, setUploadStage] = useState(null); // null | 'compressing' | 'uploading'
    const [modalImage, setModalImage] = useState(null);
    const [anexadosLocal, setAnexadosLocal] = useState(existingFiles);
    const [confirmRemove, setConfirmRemove] = useState(null); // { type: 'selected'|'existing', index|path }
    const [isDragging, setIsDragging] = useState(false);
    const [isTriggeringIA, setIsTriggeringIA] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const cancelRef = useRef();
    const toast = useToast();

    useEffect(() => {
        setAnexadosLocal(existingFiles);
    }, [existingFiles]);

    // Previews das imagens selecionadas (blob URLs) — revoga ao desmontar/trocar
    const previews = useMemo(() => {
        return selectedFiles.map(f => (f.type?.startsWith('image/') ? URL.createObjectURL(f) : null));
    }, [selectedFiles]);

    useEffect(() => {
        return () => previews.forEach(u => u && URL.revokeObjectURL(u));
    }, [previews]);

    const isUploading = uploadStage !== null;

    const validateAndAddFiles = (fileList) => {
        const files = Array.from(fileList || []);
        const ignorados = [];
        const validos = files.filter(file => {
            if (!ALLOWED_TYPES.includes(file.type)) {
                ignorados.push(`${file.name}: tipo não suportado`);
                return false;
            }
            if (file.size > MAX_FILE_SIZE) {
                ignorados.push(`${file.name}: excede 50MB`);
                return false;
            }
            return true;
        });
        if (ignorados.length) {
            toast({
                title: `${ignorados.length} arquivo(s) ignorado(s)`,
                description: ignorados.join(' • '),
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
        }
        if (validos.length) {
            setSelectedFiles(prev => [...prev, ...validos]);
        }
    };

    const handleFileSelect = (event) => {
        validateAndAddFiles(event.target.files);
        if (event.target) event.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (isUploading) return;
        validateAndAddFiles(e.dataTransfer?.files);
    };

    const removeSelectedFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingFile = async (path) => {
        const result = await removerArquivoDevolucao(devolucaoId, path);
        if (result && result.success) {
            setAnexadosLocal(prev => prev.filter(f => f !== path));
            toast({ title: 'Arquivo removido', status: 'success', duration: 2500, isClosable: true });
            if (onUploadSuccess) {
                onUploadSuccess({ removed: true, arquivo: path, total_arquivos: result.total_arquivos });
            }
        } else {
            toast({ title: 'Falha ao remover arquivo', status: 'error', duration: 4000, isClosable: true });
        }
    };

    const confirmarRemocao = async () => {
        if (!confirmRemove) return;
        if (confirmRemove.type === 'selected') {
            removeSelectedFile(confirmRemove.index);
        } else {
            await removeExistingFile(confirmRemove.path);
        }
        setConfirmRemove(null);
    };

    const openImageModal = (urlOrFile) => {
        if (typeof urlOrFile === 'string') {
            setModalImage({ url: buildFileUrl(urlOrFile), blob: false });
        } else {
            setModalImage({ url: URL.createObjectURL(urlOrFile), blob: true });
        }
    };

    const closeImageModal = () => {
        if (modalImage?.blob) URL.revokeObjectURL(modalImage.url);
        setModalImage(null);
    };

    const handleUpload = async () => {
        if (!devolucaoId) {
            toast({ title: 'ID da devolução não encontrado', status: 'error', duration: 3000, isClosable: true });
            return;
        }
        if (selectedFiles.length === 0) {
            toast({ title: 'Selecione ao menos 1 arquivo', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        const userString = window.localStorage.getItem('user');
        if (!userString || userString === 'undefined') {
            toast({ title: 'Usuário não encontrado. Faça login novamente.', status: 'error', duration: 4000, isClosable: true });
            return;
        }

        try {
            setUploadStage('compressing');
            const arquivosParaEnvio = await compressImages(selectedFiles);

            setUploadStage('uploading');
            const result = await anexarArquivosDevolucao(devolucaoId, arquivosParaEnvio, preAnalise);

            if (!result) throw new Error('Falha ao anexar arquivos');

            toast({
                title: `${selectedFiles.length} arquivo(s) anexado(s)`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setSelectedFiles([]);
            if (result?.arquivos_salvos) {
                setAnexadosLocal(prev => [...prev, ...result.arquivos_salvos]);
            }
            setPreAnalise('');
            if (onUploadSuccess) {
                onUploadSuccess({ ...result, arquivos_count: selectedFiles.length, pre_analise: preAnalise });
            }
        } catch (error) {
            console.error('Erro no upload:', error);
            let msg = 'Erro ao anexar arquivos.';
            if (error.response?.data?.message) msg = error.response.data.message;
            else if (error.response?.data?.errors) {
                msg = Object.values(error.response.data.errors).flat().join(', ');
            }
            toast({ title: 'Erro ao anexar', description: msg, status: 'error', duration: 5000, isClosable: true });
        } finally {
            setUploadStage(null);
        }
    };

    const handleAnaliseIA = async () => {
        if (!devolucaoId) return;
        setIsTriggeringIA(true);
        try {
            const res = await dispararAnaliseIADevolucao(devolucaoId);
            if (res && res.success) {
                toast({
                    title: 'Análise IA enfileirada',
                    description: res.message || 'O resultado aparecerá na pré-análise em alguns minutos.',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Não foi possível disparar análise',
                    description: res?.message || 'Verifique se há imagens anexadas.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } finally {
            setIsTriggeringIA(false);
        }
    };

    const totalAnexados = anexadosLocal.length;
    const totalSelecionados = selectedFiles.length;
    const temImagemAnexada = anexadosLocal.some(p => isImagePath(p));
    const jaTemAnaliseIA = (preAnalise || '').includes('=== ANÁLISE AUTOMÁTICA DE DEFEITOS ===');

    return (
        <Box>
            {/* SEÇÃO 1: Arquivos já anexados (grid) */}
            {totalAnexados > 0 && (
                <Box mb={5}>
                    <HStack mb={3} justify="space-between">
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">
                            Anexados <Badge colorScheme="blue" ml={1}>{totalAnexados}</Badge>
                        </Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 3, sm: 4, md: 5 }} spacing={2}>
                        {anexadosLocal.map((path, idx) => {
                            const isImg = isImagePath(path);
                            const url = buildFileUrl(path);
                            return (
                                <Box
                                    key={`ex-${idx}`}
                                    position="relative"
                                    borderRadius="md"
                                    overflow="hidden"
                                    aspectRatio={1}
                                    bg="gray.100"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                >
                                    {isImg ? (
                                        <Image
                                            src={url}
                                            alt={getFileName(path)}
                                            w="100%"
                                            h="100%"
                                            objectFit="cover"
                                            cursor="pointer"
                                            onClick={() => openImageModal(path)}
                                            fallback={
                                                <Flex w="100%" h="100%" align="center" justify="center" bg="gray.200">
                                                    <FaImages color="#888" />
                                                </Flex>
                                            }
                                        />
                                    ) : (
                                        <Flex
                                            w="100%"
                                            h="100%"
                                            align="center"
                                            justify="center"
                                            direction="column"
                                            bg="red.50"
                                            cursor="pointer"
                                            onClick={() => window.open(url, '_blank')}
                                        >
                                            <FaFilePdf size={28} color="#C53030" />
                                            <Text fontSize="10px" color="red.700" mt={1} fontWeight="bold">PDF</Text>
                                        </Flex>
                                    )}
                                    <IconButton
                                        icon={<FaTrash />}
                                        size="xs"
                                        colorScheme="red"
                                        position="absolute"
                                        top="4px"
                                        right="4px"
                                        aria-label="Remover arquivo"
                                        onClick={(e) => { e.stopPropagation(); setConfirmRemove({ type: 'existing', path }); }}
                                        borderRadius="full"
                                        boxShadow="md"
                                        opacity={0.95}
                                    />
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                </Box>
            )}

            {/* SEÇÃO 2: Ações de captura/upload (mobile-first) */}
            <VStack spacing={3} align="stretch" mb={4}>
                <HStack spacing={2}>
                    <Button
                        flex={1}
                        h="56px"
                        colorScheme="orange"
                        leftIcon={<FaCamera />}
                        onClick={() => cameraInputRef.current?.click()}
                        isDisabled={isUploading}
                        fontSize="sm"
                    >
                        Tirar foto
                    </Button>
                    <Button
                        flex={1}
                        h="56px"
                        variant="outline"
                        colorScheme="orange"
                        leftIcon={<FaImages />}
                        onClick={() => fileInputRef.current?.click()}
                        isDisabled={isUploading}
                        fontSize="sm"
                    >
                        Da galeria
                    </Button>
                </HStack>

                {/* Dropzone (apenas desktop visualmente, mas funciona em qualquer contexto) */}
                <Box
                    display={{ base: 'none', md: 'block' }}
                    border="2px dashed"
                    borderColor={isDragging ? 'orange.400' : 'gray.300'}
                    bg={isDragging ? 'orange.50' : 'transparent'}
                    borderRadius="md"
                    p={3}
                    textAlign="center"
                    fontSize="xs"
                    color="gray.500"
                    transition="all 0.15s"
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                >
                    ou arraste arquivos aqui — JPG, PNG, WEBP, GIF, PDF (até 50MB)
                </Box>

                {/* Inputs ocultos */}
                <Input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    display="none"
                />
                <Input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handleFileSelect}
                    display="none"
                />
            </VStack>

            {/* SEÇÃO 3: Preview dos selecionados */}
            {totalSelecionados > 0 && (
                <Box mb={4}>
                    <HStack mb={2} justify="space-between">
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">
                            Para enviar <Badge colorScheme="orange" ml={1}>{totalSelecionados}</Badge>
                        </Text>
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setSelectedFiles([])} isDisabled={isUploading}>
                            Limpar
                        </Button>
                    </HStack>
                    <SimpleGrid columns={{ base: 3, sm: 4, md: 5 }} spacing={2}>
                        {selectedFiles.map((file, idx) => {
                            const isPdf = file.type === 'application/pdf';
                            const preview = previews[idx];
                            return (
                                <Box
                                    key={`sel-${idx}`}
                                    position="relative"
                                    borderRadius="md"
                                    overflow="hidden"
                                    aspectRatio={1}
                                    bg="gray.100"
                                    borderWidth="2px"
                                    borderColor="orange.300"
                                >
                                    {isPdf ? (
                                        <Flex w="100%" h="100%" align="center" justify="center" direction="column" bg="red.50">
                                            <FaFilePdf size={28} color="#C53030" />
                                            <Text fontSize="9px" color="red.700" mt={1} fontWeight="bold" noOfLines={1} px={1}>
                                                {file.name}
                                            </Text>
                                        </Flex>
                                    ) : (
                                        <Image
                                            src={preview}
                                            alt={file.name}
                                            w="100%"
                                            h="100%"
                                            objectFit="cover"
                                            cursor="pointer"
                                            onClick={() => openImageModal(file)}
                                        />
                                    )}
                                    <Box position="absolute" bottom="0" left="0" right="0" bg="blackAlpha.600" px={1} py="2px">
                                        <Text fontSize="9px" color="white" textAlign="center">
                                            {formatFileSize(file.size)}
                                        </Text>
                                    </Box>
                                    <IconButton
                                        icon={<FaTrash />}
                                        size="xs"
                                        colorScheme="red"
                                        position="absolute"
                                        top="4px"
                                        right="4px"
                                        aria-label="Remover"
                                        onClick={(e) => { e.stopPropagation(); removeSelectedFile(idx); }}
                                        isDisabled={isUploading}
                                        borderRadius="full"
                                        boxShadow="md"
                                        opacity={0.95}
                                    />
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                </Box>
            )}

            {/* SEÇÃO 4: Pré-análise */}
            <FormControl mb={4}>
                <HStack mb={1} justify="space-between" align="center">
                    <FormLabel fontSize="sm" color="gray.700" mb={0}>
                        Pré-análise (opcional)
                    </FormLabel>
                    {temImagemAnexada && !jaTemAnaliseIA && (
                        <Button
                            size="xs"
                            colorScheme="purple"
                            variant="outline"
                            leftIcon={<FaMagic />}
                            onClick={handleAnaliseIA}
                            isLoading={isTriggeringIA}
                            loadingText="Enviando..."
                            isDisabled={isUploading}
                        >
                            Analisar com IA
                        </Button>
                    )}
                </HStack>
                <Textarea
                    value={preAnalise}
                    onChange={(e) => setPreAnalise(e.target.value)}
                    placeholder="Descreva o estado do produto, defeitos observados, etc."
                    size="sm"
                    rows={3}
                    maxLength={1000}
                    isDisabled={isUploading}
                />
                <Text fontSize="xs" color="gray.500" textAlign="right" mt={1}>
                    {preAnalise.length}/1000
                </Text>
            </FormControl>

            {/* SEÇÃO 5: Botão de envio + progresso */}
            {(totalSelecionados > 0 || preAnalise !== preAnaliseExistente) && (
                <Box>
                    <Button
                        w="100%"
                        h="52px"
                        colorScheme="orange"
                        leftIcon={!isUploading ? <FaUpload /> : undefined}
                        onClick={handleUpload}
                        isLoading={isUploading}
                        loadingText={uploadStage === 'compressing' ? 'Otimizando imagens...' : 'Enviando...'}
                        isDisabled={isUploading}
                        fontSize="md"
                        fontWeight="bold"
                    >
                        {totalSelecionados > 0
                            ? `Anexar ${totalSelecionados} arquivo${totalSelecionados > 1 ? 's' : ''}`
                            : 'Salvar pré-análise'}
                    </Button>
                    {isUploading && (
                        <Progress
                            mt={2}
                            size="xs"
                            isIndeterminate
                            colorScheme="orange"
                            borderRadius="full"
                        />
                    )}
                </Box>
            )}

            {/* Modal de visualização */}
            <Modal isOpen={!!modalImage} onClose={closeImageModal} size={{ base: 'full', md: '2xl' }} isCentered>
                <ModalOverlay bg="blackAlpha.800" />
                <ModalContent bg="transparent" boxShadow="none" my={{ base: 0, md: 'auto' }}>
                    <ModalCloseButton color="white" size="lg" zIndex={10} />
                    <ModalBody display="flex" alignItems="center" justifyContent="center" p={0}>
                        {modalImage && (
                            <Image
                                src={modalImage.url}
                                alt="Preview"
                                maxW="100%"
                                maxH={{ base: '100vh', md: '85vh' }}
                                objectFit="contain"
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Alerta de confirmação para remover existente */}
            <AlertDialog
                isOpen={confirmRemove?.type === 'existing'}
                leastDestructiveRef={cancelRef}
                onClose={() => setConfirmRemove(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent mx={4}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Remover arquivo?
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Esta ação é permanente e removerá o arquivo do servidor.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setConfirmRemove(null)}>
                                Cancelar
                            </Button>
                            <Button colorScheme="red" onClick={confirmarRemocao} ml={3}>
                                Remover
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default DevolucoesFileUpload;
